import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Home, LogOut, PoundSterling, Wrench, FileText, Plus,
  Loader2, Calendar, CheckCircle, Clock, AlertTriangle, Send, Camera, X, Image
} from "lucide-react";
import { API } from "@/App";

const getPortalAuth = () => {
  const token = localStorage.getItem("portal_token");
  const client = JSON.parse(localStorage.getItem("portal_client") || "{}");
  return { token, client };
};

export const TenantDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenancies, setTenancies] = useState([]);
  const [selectedTenancy, setSelectedTenancy] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { token, client } = getPortalAuth();

  const [maintenanceForm, setMaintenanceForm] = useState({
    title: "",
    description: "",
    category: "general",
    priority: "medium"
  });
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    if (!token || client.client_type !== "tenant") {
      navigate("/portal/login");
      return;
    }
    fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/portal/tenant/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenancies(response.data.tenancies || []);
      if (response.data.tenancies?.length > 0) {
        setSelectedTenancy(response.data.tenancies[0]);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("portal_token");
        navigate("/portal/login");
      }
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_client");
    navigate("/portal/login");
  };

  const handleSubmitMaintenance = async () => {
    if (!maintenanceForm.title || !maintenanceForm.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/portal/tenant/maintenance/request`, {
        tenancy_id: selectedTenancy.tenancy.tenancy_id,
        ...maintenanceForm,
        photos: photos.map(p => p.url) // Include photo URLs
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Maintenance request submitted!");
      setShowMaintenanceModal(false);
      setMaintenanceForm({ title: "", description: "", category: "general", priority: "medium" });
      setPhotos([]); // Clear photos
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (photos.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }

    setUploadingPhoto(true);
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(`${API}/portal/tenant/upload-photo`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });

        if (response.data.url) {
          setPhotos(prev => [...prev, { 
            url: response.data.url, 
            name: file.name,
            preview: URL.createObjectURL(file)
          }]);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        console.error("Upload error:", error);
      }
    }
    
    setUploadingPhoto(false);
    e.target.value = ""; // Reset input
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const formatCurrency = (amount) => `£${parseFloat(amount || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-slate-800">Tenant Portal</h1>
                <p className="text-sm text-slate-500">Welcome, {client?.name}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {tenancies.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Home className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h2 className="text-xl font-semibold">No Tenancies Found</h2>
              <p className="text-slate-500 mt-2">You don&apos;t have any tenancies linked to your account.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-4 gap-6">
            {tenancies.length > 1 && (
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-slate-500">Your Properties</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {tenancies.map((t) => (
                      <button
                        key={t.tenancy.tenancy_id}
                        onClick={() => setSelectedTenancy(t)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedTenancy?.tenancy.tenancy_id === t.tenancy.tenancy_id
                            ? "bg-purple-600 text-white"
                            : "bg-slate-50 hover:bg-slate-100"
                        }`}
                      >
                        <p className="font-medium truncate">{t.tenancy.property_address || t.tenancy.address}</p>
                        {t.unpaid_rent_count > 0 && (
                          <Badge className="mt-1 bg-red-500 text-white">
                            {t.unpaid_rent_count} unpaid
                          </Badge>
                        )}
                      </button>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}

            <div className={tenancies.length > 1 ? "lg:col-span-3" : "lg:col-span-4"}>
              {selectedTenancy && (
                <div className="space-y-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-xl font-semibold">
                            {selectedTenancy.tenancy.property_address || selectedTenancy.tenancy.address}
                          </h2>
                          <p className="text-slate-500">
                            Rent: {formatCurrency(selectedTenancy.tenancy.rent_amount)}/month
                          </p>
                          {selectedTenancy.tenancy.tenancy_end_date && (
                            <p className="text-sm text-slate-400 mt-1">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              Tenancy ends: {new Date(selectedTenancy.tenancy.tenancy_end_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <Badge className={
                          selectedTenancy.tenancy.status === "active" ? "bg-green-100 text-green-700" :
                          "bg-slate-100 text-slate-700"
                        }>
                          {selectedTenancy.tenancy.status?.toUpperCase() || "ACTIVE"}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  <Tabs defaultValue="rent" className="space-y-4">
                    <TabsList>
                      <TabsTrigger value="rent">
                        <PoundSterling className="w-4 h-4 mr-2" />
                        Rent Payments
                        {selectedTenancy.unpaid_rent_count > 0 && (
                          <Badge className="ml-1 bg-red-500 text-white">{selectedTenancy.unpaid_rent_count}</Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="maintenance">
                        <Wrench className="w-4 h-4 mr-2" />
                        Maintenance
                      </TabsTrigger>
                      <TabsTrigger value="documents">
                        <FileText className="w-4 h-4 mr-2" />
                        Documents
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="rent">
                      <Card>
                        <CardHeader>
                          <CardTitle>Rent Payment History</CardTitle>
                          <CardDescription>Your recent rent payments</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {selectedTenancy.recent_rent?.length === 0 ? (
                            <p className="text-slate-500 text-center py-4">No rent records</p>
                          ) : (
                            <div className="space-y-3">
                              {selectedTenancy.recent_rent?.map((r, i) => (
                                <div key={i} className={`flex items-center justify-between p-4 rounded-lg ${
                                  r.status === "overdue" ? "bg-red-50" :
                                  r.status === "due" ? "bg-amber-50" :
                                  "bg-slate-50"
                                }`}>
                                  <div>
                                    <p className="font-medium">{formatCurrency(r.amount)}</p>
                                    <p className="text-sm text-slate-500">
                                      Due: {new Date(r.due_date).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <Badge className={
                                      r.status === "paid" ? "bg-green-100 text-green-700" :
                                      r.status === "overdue" ? "bg-red-100 text-red-700" :
                                      r.status === "partial" ? "bg-amber-100 text-amber-700" :
                                      "bg-slate-100 text-slate-700"
                                    }>
                                      {r.status === "paid" && <CheckCircle className="w-3 h-3 mr-1" />}
                                      {r.status === "overdue" && <AlertTriangle className="w-3 h-3 mr-1" />}
                                      {r.status?.toUpperCase()}
                                    </Badge>
                                    {r.paid_date && (
                                      <p className="text-xs text-slate-400 mt-1">
                                        Paid: {new Date(r.paid_date).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="maintenance">
                      <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                          <div>
                            <CardTitle>Maintenance Requests</CardTitle>
                            <CardDescription>Report issues with your property</CardDescription>
                          </div>
                          <Button onClick={() => setShowMaintenanceModal(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            New Request
                          </Button>
                        </CardHeader>
                        <CardContent>
                          {selectedTenancy.maintenance_requests?.length === 0 ? (
                            <div className="text-center py-8">
                              <Wrench className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                              <p className="text-slate-500">No maintenance requests</p>
                              <Button className="mt-4" onClick={() => setShowMaintenanceModal(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Report an Issue
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {selectedTenancy.maintenance_requests?.map((job) => (
                                <div key={job.job_id} className="p-4 border rounded-lg">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className="font-medium">{job.title}</p>
                                      <p className="text-sm text-slate-500">{job.description}</p>
                                      <p className="text-xs text-slate-400 mt-2">
                                        Reported: {new Date(job.created_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                    <Badge className={
                                      job.status === "completed" ? "bg-green-100 text-green-700" :
                                      job.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                      job.status === "scheduled" ? "bg-purple-100 text-purple-700" :
                                      "bg-amber-100 text-amber-700"
                                    }>
                                      {job.status === "completed" && <CheckCircle className="w-3 h-3 mr-1" />}
                                      {job.status === "in_progress" && <Clock className="w-3 h-3 mr-1" />}
                                      {job.status?.replace(/_/g, " ").toUpperCase()}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="documents">
                      <Card>
                        <CardHeader>
                          <CardTitle>Tenancy Documents</CardTitle>
                          <CardDescription>Your tenancy agreement and related documents</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="text-center py-8">
                            <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                            <p className="text-slate-500">Documents will appear here</p>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Maintenance Request Modal */}
      <Dialog open={showMaintenanceModal} onOpenChange={setShowMaintenanceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Maintenance Issue</DialogTitle>
            <DialogDescription>
              Describe the issue and we&apos;ll arrange for it to be fixed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Issue Title *</Label>
              <Input
                value={maintenanceForm.title}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, title: e.target.value })}
                placeholder="e.g., Leaking tap in bathroom"
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={maintenanceForm.description}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                placeholder="Please describe the issue in detail..."
                rows={4}
              />
            </div>

            {/* Photo Upload Section */}
            <div className="space-y-2">
              <Label>Photos (Optional)</Label>
              <p className="text-xs text-muted-foreground">Upload up to 5 photos to help describe the issue</p>
              
              <div className="flex flex-wrap gap-2 mt-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden border">
                    <img 
                      src={photo.preview || photo.url} 
                      alt={photo.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {photos.length < 5 && (
                  <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                    {uploadingPhoto ? (
                      <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    ) : (
                      <>
                        <Camera className="w-5 h-5 text-slate-400" />
                        <span className="text-xs text-slate-400 mt-1">Add</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploadingPhoto}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={maintenanceForm.category}
                  onValueChange={(v) => setMaintenanceForm({ ...maintenanceForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="heating">Heating</SelectItem>
                    <SelectItem value="appliances">Appliances</SelectItem>
                    <SelectItem value="structural">Structural</SelectItem>
                    <SelectItem value="garden">Garden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={maintenanceForm.priority}
                  onValueChange={(v) => setMaintenanceForm({ ...maintenanceForm, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintenanceModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitMaintenance} disabled={submitting}>
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDashboard;
